import { Component, OnInit, Input, AfterViewInit } from '@angular/core';
import { Chart, ChartData, ChartOptions } from 'chart.js';
import { BidMatch, BidRecord, DashboardRecord, WaterRequestService } from '../../../../services/water-request-service';
import { Guid } from 'guid-typescript';

interface ChartPoint {
  date: string;
  volume: number;
}

@Component({
  selector: 'ws-trade-stats',
  templateUrl: './ws-trade-stats.component.html',
  styleUrls: ['./ws-trade-stats.component.css'],
  standalone: false
})
export class WsTradeStatsComponent implements OnInit{
  @Input() type: 'trade' | 'share' = 'trade';
  @Input() userClientId!: string;

  activeTabGroup: 'global' | 'personal' = 'global';
  adminUrl = '';
  private matches: BidMatch[] = [];
  private bidRecords: Record<string, BidRecord> = {};


  globalStats: {
    tradesToDate: number;
    tradesProposed: number;
    volumeTraded: number;
    chartData: ChartPoint[];
  } = { tradesToDate: 0, tradesProposed: 0, volumeTraded: 0, chartData: [] };

  personalStats: {
    tradesToDate: number;
    tradesProposed: number;
    volumeTraded: number;
    chartData: ChartPoint[];
  } = { tradesToDate: 0, tradesProposed: 0, volumeTraded: 0, chartData: [] };

  private globalChart?: Chart;
  private personalChart?: Chart;

  constructor(private waterService: WaterRequestService) { }

  ngOnInit(): void {
    this.loadDashboardData();
  }

  //ngAfterViewInit(): void {
  //  this.initChart('stat-chart-global', this.globalStats.chartData, '#007bff');
  //  this.initChart('stat-chart-personal', this.personalStats.chartData, '#28a745');
  //}

  private loadDashboardData(): void {
    this.waterService.fullyApprovedMatches((matches: BidMatch[]) => {
      this.matches = matches;

      // Fetch BidRecords for all matches
      const fetchPromises = matches.map(match =>
        Promise.all([
          new Promise<void>(resolve =>
            this.waterService.recordInfo(match.waterBidId1, match.clientId1, data => {
              this.bidRecords[match.waterBidId1] = data;
              resolve();
            })
          ),
          new Promise<void>(resolve =>
            this.waterService.recordInfo(match.waterBidId2, match.clientId2, data => {
              this.bidRecords[match.waterBidId2] = data;
              resolve();
            })
          )
        ])
      );

      Promise.all(fetchPromises).then(() => {
        // Filter matches by the selected type
        const filteredMatches = matches.filter(m => this.getMatchMode(m) === this.type);
        this.processBidMatches(filteredMatches);
      });
    });
  }


  private getMatchMode(match: BidMatch): 'trade' | 'share' {
    const bid1 = this.bidRecords[match.waterBidId1];
    const bid2 = this.bidRecords[match.waterBidId2];

    // If either bid is missing, fallback to 'trade'
    if (!bid1 || !bid2) return 'trade';

    // Check mode (case-insensitive)
    if (bid1.mode.toLowerCase() === 'share' || bid2.mode.toLowerCase() === 'share') {
      return 'share';
    }
    return 'trade';
  }

  private buildTimeline(start: Date, end: Date): ChartPoint[] {
    const timeline: ChartPoint[] = [];
    const d = new Date(start);
    d.setHours(0, 0, 0, 0);

    while (d <= end) {
      timeline.push({
        date: d.toISOString().slice(0, 10),
        volume: 0
      });
      d.setDate(d.getDate() + 1);
    }

    return timeline;
  }

  private processBidMatches(matches: BidMatch[]): void {
    if (!matches.length) return;

    const globalVolumeByDate: Record<string, number> = {};
    const personalVolumeByDate: Record<string, number> = {};

    let globalTrades = 0;
    let personalTrades = 0;
    let globalVolume = 0;
    let personalVolume = 0;

    // Determine full date range
    const starts = matches
      .filter(m => m.matchStart)
      .map(m => new Date(m.matchStart!));
    const ends = matches
      .filter(m => m.matchEnd)
      .map(m => new Date(m.matchEnd!));

    const minDate = new Date(Math.min(...starts.map(d => d.getTime())));
    const maxDate = new Date(Math.max(...ends.map(d => d.getTime())));

    matches.forEach(match => {
      if (!match.totalVolume || !match.matchStart || !match.matchEnd) return;

      globalTrades++;
      globalVolume += match.totalVolume;

      const startDate = new Date(match.matchStart);
      const endDate = new Date(match.matchEnd);
      const days =
        Math.floor((endDate.getTime() - startDate.getTime()) / 86400000) + 1;

      const volumePerDay = match.totalVolume / days;

      const isPersonal =
        !!this.userClientId &&
        (match.clientId1 === this.userClientId ||
          match.clientId2 === this.userClientId);

      for (
        let d = new Date(startDate);
        d <= endDate;
        d.setDate(d.getDate() + 1)
      ) {
        const key = d.toISOString().slice(0, 10);
        globalVolumeByDate[key] =
          (globalVolumeByDate[key] || 0) + volumePerDay;

        if (isPersonal) {
          personalVolumeByDate[key] =
            (personalVolumeByDate[key] || 0) + volumePerDay;
        }
      }

      if (isPersonal) {
        personalTrades++;
        personalVolume += match.totalVolume;
      }
    });


    // Build timelines
    this.globalStats.chartData = this.buildTimeline(minDate, maxDate).map(d => ({
      date: d.date,
      volume: globalVolumeByDate[d.date] ?? 0
    }));

    this.personalStats.chartData = this.buildTimeline(minDate, maxDate).map(d => ({
      date: d.date,
      volume: personalVolumeByDate[d.date] ?? 0
    }));

    // Summary stats
    this.globalStats.tradesToDate = globalTrades;
    this.globalStats.tradesProposed = globalTrades;
    this.globalStats.volumeTraded = globalVolume;

    this.personalStats.tradesToDate = personalTrades;
    this.personalStats.tradesProposed = personalTrades;
    this.personalStats.volumeTraded = personalVolume;

    // Initialize or update charts
    if (!this.globalChart) {
      this.initChart('stat-chart-global', this.globalStats.chartData, '#007bff');
    } else {
      this.updateChart(this.globalChart, this.globalStats.chartData);
    }

    if (!this.personalChart) {
      this.initChart('stat-chart-personal', this.personalStats.chartData, '#28a745');
    } else {
      this.updateChart(this.personalChart, this.personalStats.chartData);
    }
  }

  private initChart(canvasId: string, data: ChartPoint[], color: string): void {
    const canvas = document.getElementById(canvasId) as HTMLCanvasElement;
    if (!canvas) return;

    const chart = new Chart(canvas, {
      type: 'line',
      data: {
        labels: data.map(d => d.date),
        datasets: [{
          data: data.map(d => d.volume),
          borderColor: color,
          borderWidth: 2,
          fill: false
        }]
      },
      options: this.chartOptions()
    });

    if (canvasId === 'stat-chart-global') this.globalChart = chart;
    if (canvasId === 'stat-chart-personal') this.personalChart = chart;
  }

  private updateChart(chart: Chart | undefined, data: ChartPoint[]): void {
    if (!chart) return;
    chart.data.labels = data.map(d => d.date);
    chart.data.datasets[0].data = data.map(d => d.volume);
    chart.update();
  }

  private chartOptions(): ChartOptions {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        title: {
          display: true,
          text: 'Ongoing Trades',
          color: '#111111',
          font: { size: 18, weight: 'bold' },
          align: 'center'
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value = context.parsed.y;
              return `Total Volume: ${value} bbl`;
            }
          }
        }
      },
      scales: {
        x: {},
        y: {
          beginAtZero: true,
          title: { display: true, text: 'Volume (bbl)' }
        }
      }
    };
  }

 
  downloadCsv(): void {
    if (!this.matches.length || !this.userClientId) return;

    const personalMatches = this.matches
      .filter(m =>
        m.matchStart &&
        m.matchEnd &&
        m.totalVolume &&
        (m.clientId1 === this.userClientId || m.clientId2 === this.userClientId) &&
        this.getMatchMode(m) === this.type 
      );


    if (!personalMatches.length) return;

    const csvRows: string[][] = [
      [
        'Match Date',
        'Match Volume',
        'Providing Trucking Volume',
        'Providing Piping Volume',
        'Obtaining Trucking Volume',
        'Obtaining Piping Volume',
        'Quality'
      ]
    ];

    // Helper to fetch BidRecords for a match
    const fetchBidRecords = (match: BidMatch): Promise<{ provide: BidRecord; need: BidRecord }> => {
      return new Promise((resolve, reject) => {
        let bid1: BidRecord | null = null;
        let bid2: BidRecord | null = null;

        this.waterService.recordInfo(match.waterBidId1, match.clientId1, data => {
          bid1 = data;
          if (bid2) resolve(assignProvideNeed(bid1!, bid2!));
        });

        this.waterService.recordInfo(match.waterBidId2, match.clientId2, data => {
          bid2 = data;
          if (bid1) resolve(assignProvideNeed(bid1!, bid2!));
        });

        // Helper to determine which is provide/need
        const assignProvideNeed = (b1: BidRecord, b2: BidRecord) => {
          if (b1.provideOrNeed.toLowerCase() === 'provide') {
            return { provide: b1, need: b2 };
          } else {
            return { provide: b2, need: b1 };
          }
        };
      });
    };

    // Process all matches asynchronously
    Promise.all(personalMatches.map(fetchBidRecords)).then(results => {
      results.forEach((pair, index) => {
        const match = personalMatches[index];
        const { provide, need } = pair;

        const startDate = new Date(match.matchStart!);
        const endDate = new Date(match.matchEnd!);
        const days = Math.floor((endDate.getTime() - startDate.getTime()) / 86400000) + 1;
        const dailyVolume = match.totalVolume! / days;

        for (let i = 0; i < days; i++) {
          const date = new Date(startDate);
          date.setDate(date.getDate() + i);

          csvRows.push([
            date.toLocaleDateString(),
            dailyVolume.toString(),
            provide.provideTrucksCapacity?.toString() ?? '-',
            provide.provideLayflatsCapacity?.toString() ?? '-',
            need.canAcceptTrucks ? dailyVolume.toString() : '-',
            need.canAcceptLayflats ? dailyVolume.toString() : '-',
            provide.waterQuality ?? 'Best match did not include quality details'
          ]);
        }
      });

      // Generate CSV
      const csv = csvRows.map(r => r.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', 'my_trade_matches.csv');
      link.click();
    });
  }

}
